import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getMySubscription,
  getMyUsage,
} from "../../redux/Apps/users/userSlice";

const SubscriptionContext = createContext();

const BAD_STATUSES = new Set([
  "expired",
  "inactive",
  "cancelled",
  "canceled",
  "failed",
  "past_due",
  "payment_failed",
]);

const ACTIVE_STATUSES = new Set([
  "active",
  "trial",
  "trialing",
  "in_trial",
  "authorized",
  "authorised",
  "grace",
  "grace_period",
  "paid",
  "payment_success",
  "completed",
  "success",
]);

export const isDevMode = ["localhost", "127.0.0.1", "::1"].includes(
  window.location.hostname
);
// console.log("Is Dev Mode:", isDevMode);
export const SubscriptionProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [rehydrated, setRehydrated] = useState(false);
  const subscriptionRequestKeyRef = useRef(null);
  const {
    token,
    teamMemberLawyerId,
    mySubscription,
    subscriptionLoading,
    userPlanAccessLimit,
    plan: storedPlan,
    subscriptionError,
    subscriptionFetched,
    subscriptionStatusCode,
    subscriptionStatusMessage,
  } = useSelector((state) => state.user);

  useEffect(() => {
    if (!token) {
      subscriptionRequestKeyRef.current = null;
      return;
    }

    dispatch(getMyUsage({ token, teamMemberLawyerId }));
  }, [token, dispatch, teamMemberLawyerId]);

  const activePlan = useMemo(() => {
    if (!mySubscription || typeof mySubscription !== "object") return null;
    const candidates = [
      mySubscription.plan,
      mySubscription.plan_details,
      mySubscription.subscription_plan,
      mySubscription.current_plan,
    ];

    for (const candidate of candidates) {
      if (candidate && typeof candidate === "object") {
        return candidate;
      }
    }
    if (storedPlan && typeof storedPlan === "object") {
      return storedPlan;
    }
    return null;
  }, [mySubscription, storedPlan]);

  const hasPlanIdentifier = useMemo(() => {
    if (!mySubscription) return false;
    const candidates = [
      mySubscription.plan_id,
      mySubscription.planId,
      activePlan?.id,
      activePlan?.plan_id,
      activePlan?.planId,
      mySubscription.subscription_plan_id,
      mySubscription.plan?.id,
      mySubscription.plan?.plan_id,
      mySubscription.plan?.planId,
      mySubscription.plan_details?.id,
      mySubscription.plan_details?.plan_id,
      storedPlan?.id,
      storedPlan?.plan_id,
    ];

    return candidates.some((candidate) => {
      if (candidate === null || candidate === undefined) return false;
      const str = String(candidate).trim();
      return str !== "" && str.toLowerCase() !== "null";
    });
  }, [mySubscription, activePlan, storedPlan]);

  const subscriptionErrorMessage = useMemo(() => {
    const resolvedError = Array.isArray(subscriptionError)
      ? subscriptionError[0]
      : subscriptionError;

    const messageCandidates = [
      subscriptionStatusMessage,
      typeof resolvedError === "string" ? resolvedError : null,
      resolvedError?.message,
      resolvedError?.detail,
      resolvedError?.error,
      Array.isArray(resolvedError?.non_field_errors)
        ? resolvedError.non_field_errors[0]
        : null,
    ];

    const message = messageCandidates.find(
      (value) => typeof value === "string" && value.trim() !== ""
    );

    if (message) {
      return message;
    }

    // if (
    //   typeof subscriptionStatusCode === "string" &&
    //   subscriptionStatusCode.toLowerCase() === SUBSCRIPTION_INACTIVE_CODE
    // ) {
    //   return SUBSCRIPTION_INACTIVE_MESSAGE;
    // }

    return null;
  }, [subscriptionError, subscriptionStatusMessage]);

  // Mark as rehydrated after short delay
  useEffect(() => {
    const timer = setTimeout(() => setRehydrated(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const fetchSubscription = useCallback(() => {
    const requestKey = `${token || ""}:${teamMemberLawyerId || "self"}`;

    if (
      token &&
      rehydrated &&
      !subscriptionFetched &&
      !subscriptionLoading &&
      subscriptionRequestKeyRef.current !== requestKey
    ) {
      subscriptionRequestKeyRef.current = requestKey;
      dispatch(getMySubscription({ token, teamMemberLawyerId }))
        .unwrap()
        .then(() => {
          dispatch(getMyUsage({ token, teamMemberLawyerId }));
        })
        .catch(() => {});
    }
  }, [
    token,
    rehydrated,
    subscriptionFetched,
    subscriptionLoading,
    dispatch,
    teamMemberLawyerId,
  ]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // READY flag: true when rehydrated and not loading
  const ready = useMemo(
    () => rehydrated && !subscriptionLoading,
    [rehydrated, subscriptionLoading]
  );

  /** ----------------- Utilities ------------------- **/

  const normalizeToBoolean = (value) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value > 0;
    if (typeof value === "string") {
      const lowered = value.trim().toLowerCase();
      if (["true", "1", "yes", "active", "enabled", "valid"].includes(lowered))
        return true;
      if (
        ["false", "0", "no", "inactive", "disabled", "invalid"].includes(
          lowered
        )
      )
        return false;
    }
    return null;
  };

  const isTrial = useMemo(() => {
    if (!mySubscription) return false;
    if (typeof mySubscription.is_trial === "boolean")
      return mySubscription.is_trial;
    if (typeof mySubscription.is_trail === "boolean")
      return mySubscription.is_trail;
    if (typeof mySubscription.trial === "boolean") return mySubscription.trial;
    if (typeof mySubscription.trial_active === "boolean")
      return mySubscription.trial_active;
    if (typeof mySubscription.subscription_type === "string")
      return mySubscription.subscription_type.toLowerCase().includes("trial");
    return false;
  }, [mySubscription]);

  const getExpiryDate = useCallback(() => {
    if (!mySubscription) return null;

    const candidateFields = [
      mySubscription.expires_on,
      mySubscription.expiry_on,
      mySubscription.expiry_date,
      mySubscription.expiration_date,
      mySubscription.expires_at,
      mySubscription.expired_on,
      mySubscription.end_date,
      mySubscription.end_at,
      mySubscription.ends_on,
      mySubscription.valid_till,
      mySubscription.valid_until,
      mySubscription.current_period_end,
      mySubscription.period_end,
      mySubscription.trial_ends_at,
    ];

    const raw = candidateFields.find((value) => {
      if (value === null || value === undefined) return false;
      if (value instanceof Date) return true;
      if (typeof value === "string") return value.trim() !== "";
      return typeof value === "number";
    });

    if (!raw) return null;

    const ensureEndOfDay = (date) => {
      const copy = new Date(date.getTime());
      copy.setHours(23, 59, 59, 999);
      return copy;
    };

    if (raw instanceof Date) {
      return ensureEndOfDay(raw);
    }

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return null;

    const rawStr = typeof raw === "string" ? raw.trim() : "";

    if (/^\d{4}-\d{2}-\d{2}$/.test(rawStr)) {
      return new Date(
        parsed.getFullYear(),
        parsed.getMonth(),
        parsed.getDate(),
        23,
        59,
        59,
        999
      );
    }

    if (rawStr.includes("T") && rawStr.endsWith("Z")) {
      const hasMidnightTimestamp =
        parsed.getUTCHours() === 0 &&
        parsed.getUTCMinutes() === 0 &&
        parsed.getUTCSeconds() === 0;

      if (hasMidnightTimestamp) {
        return new Date(parsed.getTime() + 24 * 60 * 60 * 1000 - 1);
      }

      return new Date(parsed.getTime());
    }

    return ensureEndOfDay(parsed);
  }, [mySubscription]);

  const normalizedStatus = useMemo(() => {
    // if (
    //   typeof subscriptionStatusCode === "string" &&
    //   subscriptionStatusCode.toLowerCase() === SUBSCRIPTION_INACTIVE_CODE
    // ) {
    //   return "inactive";
    // }
    if (!mySubscription) return null;
    const statusCandidates = [
      mySubscription.status,
      mySubscription.subscription_status,
      mySubscription.current_status,
      mySubscription.plan_status,
      mySubscription.payment_status,
    ];

    const firstStatus = statusCandidates.find(
      (value) => typeof value === "string" && value.trim() !== ""
    );

    return firstStatus ? firstStatus.trim().toLowerCase() : null;
  }, [mySubscription]);

  const hasActivePlan = useCallback(() => {
    // if (
    //   typeof subscriptionStatusCode === "string" &&
    //   subscriptionStatusCode.toLowerCase() === SUBSCRIPTION_INACTIVE_CODE
    // ) {
    //   return false;
    // }

    if (!mySubscription && !storedPlan) return false;

    const expiryDate = getExpiryDate();
    const expiredByDate = expiryDate
      ? Date.now() > expiryDate.getTime()
      : false;

    if (expiredByDate) return false;

    const statusIsBad = normalizedStatus
      ? BAD_STATUSES.has(normalizedStatus)
      : false;
    if (statusIsBad) return false;

    const normalizedActiveFlag = normalizeToBoolean(
      mySubscription.is_active ??
        mySubscription.active ??
        mySubscription.isActive ??
        mySubscription.subscription_active ??
        mySubscription.has_access
    );

    const isActiveFlag =
      normalizedActiveFlag !== null
        ? normalizedActiveFlag
        : normalizedStatus
        ? ACTIVE_STATUSES.has(normalizedStatus)
        : true;

    if (!isActiveFlag) return false;

    if (
      hasPlanIdentifier ||
      (activePlan && typeof activePlan === "object") ||
      (storedPlan && typeof storedPlan === "object")
    ) {
      return true;
    }

    if (isTrial) return true;

    const additionalFlags = [
      normalizeToBoolean(mySubscription.valid),
      normalizeToBoolean(mySubscription.granted),
      normalizeToBoolean(mySubscription.is_granted),
    ];

    if (additionalFlags.some((flag) => flag)) return true;

    if (typeof mySubscription.subscription_type === "string") return true;

    return false;
  }, [
    mySubscription,
    normalizedStatus,
    getExpiryDate,
    hasPlanIdentifier,
    activePlan,
    isTrial,
    storedPlan,
  ]);

  const subscriptionExpiryInfo = useMemo(() => {
    // if (
    //   typeof subscriptionStatusCode === "string" &&
    //   subscriptionStatusCode.toLowerCase() === SUBSCRIPTION_INACTIVE_CODE
    // ) {
    //   return {
    //     expiryDate: null,
    //     endedReason: "inactive",
    //     isExpired: true,
    //     message: subscriptionErrorMessage || SUBSCRIPTION_INACTIVE_MESSAGE,
    //   };
    // }

    const expiryDate = getExpiryDate();
    const expiredByDate = expiryDate
      ? expiryDate.getTime() <= Date.now()
      : false;

    let endedReason = null;

    if (normalizedStatus && BAD_STATUSES.has(normalizedStatus)) {
      endedReason = normalizedStatus.includes("cancel")
        ? "cancelled"
        : "expired";
    } else if (expiredByDate) {
      endedReason = "expired";
    }

    return {
      expiryDate,
      endedReason,
      isExpired: endedReason === "expired",
      message: endedReason ? subscriptionErrorMessage : null,
    };
  }, [getExpiryDate, normalizedStatus, subscriptionErrorMessage]);

  const isSubscriptionEnding = useMemo(() => {
    const expiryDate = getExpiryDate();
    if (!expiryDate) return false;
    const days = Math.ceil(
      (expiryDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)
    );
    return days >= 0 && days <= 7;
  }, [getExpiryDate]);

  const hasFeature = useCallback(
    (featureId) => {
      if (!hasActivePlan()) return false;
      if (!featureId) return true;

      const features = activePlan?.features;
      if (!Array.isArray(features) || features.length === 0) {
        return true;
      }

      const targetId = String(featureId);

      return features.some((feature) => {
        if (!feature || typeof feature !== "object") return false;
        const ids = [
          feature.id,
          feature.feature_id,
          feature.featureId,
          feature.code,
        ].filter((value) => value !== null && value !== undefined);

        return ids.some((value) => String(value) === targetId);
      });
    },
    [hasActivePlan, activePlan]
  );

  const { allowedPaths, excludedPaths, hasFeatureMetadata } = useMemo(() => {
    const allowed = new Set();
    const excluded = new Set();

    const collect = (feature) => {
      if (!feature || typeof feature !== "object") return;
      if (feature.path) allowed.add(feature.path);
      if (feature.excludePaths)
        feature.excludePaths.forEach((p) => excluded.add(p));
      if (Array.isArray(feature.subMenu)) feature.subMenu.forEach(collect);
    };

    const features = activePlan?.features ?? [];
    features.forEach(collect);

    // ✅ Always include debug path in dev mode
    if (isDevMode) {
      allowed.add("/dashboard/settings/debug-sub");
    }

    const result = {
      allowedPaths: Array.from(allowed),
      excludedPaths: Array.from(excluded),
      hasFeatureMetadata: features.length > 0,
    };

    return result;
  }, [activePlan]);

  // useEffect(() => {
  //   console.log("🔍 Allowed paths from plan:", Array.from(allowedPaths));
  //   console.log("🔍 Excluded paths from plan:", Array.from(excludedPaths));
  //   console.log("🔍 hasFeatureMetadata:", hasFeatureMetadata);
  // }, [allowedPaths, excludedPaths, hasFeatureMetadata]);

  const normalizePath = (path) => {
    // this function help to user navigate in sub rotes like /task, /task/12/(/task/:id)
    if (!path || typeof path !== "string") return "";

    let cleaned = path
      .trim()
      .replace(/\?.*$/, "") // remove query string
      .replace(/\/+$/, "") // remove trailing slash
      .replace(/\/\/+/g, "/") // collapse multiple slashes
      .replace(/\/:\w+/g, ""); // strip dynamic params like /:id

    // always begin with a single slash
    if (!cleaned.startsWith("/")) cleaned = "/" + cleaned;

    return cleaned;
  };

  const hasPath = useCallback(
    (routePath) => {
      if (!hasActivePlan()) return false;
      if (!hasFeatureMetadata) return true;

      const cleaned = normalizePath(routePath);

      if (cleaned === "/dashboard") {
        return true;
      }

      // Exclude explicitly restricted paths
      for (const ex of excludedPaths) {
        const exNorm = normalizePath(ex);
        if (cleaned === exNorm || cleaned.startsWith(`${exNorm}/`))
          return false;
      }

      // Match allowed paths
      for (const al of allowedPaths) {
        const allowed = normalizePath(al);

        // 1 Exact match
        if (cleaned === allowed) return true;

        // 2 Wildcard: allow all subpaths under /dashboard/settings/*
        if (al.endsWith("/*")) {
          const base = allowed.replace(/\/\*$/, "");
          if (cleaned.startsWith(base)) return true;
        }

        // 3 Dynamic param match like /dashboard/companies/:id
        if (al.includes("/:")) {
          const pattern = new RegExp(
            "^" + allowed.replace(/:\w+/g, "[^/]+") + "$"
          );
          if (pattern.test(cleaned)) return true;
        }
        if (cleaned.startsWith(`${allowed}/`)) return true; // this return true when parent url is allow it make the sub url true
      }
      //  No match found
      return false;
    },
    [hasActivePlan, hasFeatureMetadata, allowedPaths, excludedPaths]
  );

  const getCurrentKeyLimit = useCallback(
    (key) => {
      if (!hasActivePlan() || !key) return 0;

      if (!userPlanAccessLimit || typeof userPlanAccessLimit !== "object")
        return 0;

      let normalizedKey = key.trim();

      // If the key starts with "current_", remove it
      if (normalizedKey.startsWith("current_")) {
        normalizedKey = normalizedKey.replace(/^current_/, "");
      }

      // Optional: make it case-insensitive
      normalizedKey = normalizedKey.toLowerCase();

      const value = userPlanAccessLimit[normalizedKey];
      return value ?? 0;
    },
    [hasActivePlan, userPlanAccessLimit]
  );

  const isLimitReached = useCallback(
    (resourceKey) => {
      if (!resourceKey || !hasActivePlan()) return true;

      if (getCurrentKeyLimit(resourceKey) === Number("0")) {
        return true;
      }
      return false;
    },
    [getCurrentKeyLimit, hasActivePlan]
  );

  const getPlanName = useCallback(() => {
    if (!hasActivePlan()) return "No Plan";

    const nameCandidates = [
      activePlan?.name,
      activePlan?.plan_name,
      activePlan?.display_name,
      mySubscription?.plan?.name,
      storedPlan?.name,
      storedPlan?.plan_name,
    ];

    const planName = nameCandidates.find(
      (value) => typeof value === "string" && value.trim() !== ""
    );

    return planName ? planName : "No Plan";
  }, [hasActivePlan, activePlan, mySubscription, storedPlan]);

  /** ----------------- Provided Context ------------------- **/
  const normalizedErrorCode = useMemo(() => {
    if (typeof subscriptionStatusCode === "string") {
      return subscriptionStatusCode.toLowerCase();
    }
    return subscriptionStatusCode || null;
  }, [subscriptionStatusCode]);

  const subscriptionMessage = useMemo(
    () => subscriptionExpiryInfo.message || subscriptionErrorMessage || null,
    [subscriptionExpiryInfo, subscriptionErrorMessage]
  );

  const value = useMemo(
    () => ({
      subscription: mySubscription,
      loading: subscriptionLoading,
      ready,
      rehydrated,
      token,

      hasActivePlan,
      plan: activePlan || storedPlan || null,
      isTrial,
      isSubscriptionEnding,
      isSubscriptionExpired: subscriptionExpiryInfo.isExpired,
      subscriptionEndedReason: subscriptionExpiryInfo.endedReason,
      expiryDate: subscriptionExpiryInfo.expiryDate,
      subscriptionStatus: normalizedStatus,
      subscriptionError,
      subscriptionErrorCode: normalizedErrorCode,
      subscriptionMessage,
      subscriptionFetched,

      hasFeature,
      hasPath,
      getCurrentKeyLimit,
      isLimitReached,
      getPlanName,
    }),
    [
      mySubscription,
      subscriptionLoading,
      ready,
      rehydrated,
      token,
      hasActivePlan,
      activePlan,
      storedPlan,
      isTrial,
      isSubscriptionEnding,
      subscriptionExpiryInfo,
      normalizedStatus,
      subscriptionError,
      normalizedErrorCode,
      subscriptionMessage,
      subscriptionFetched,
      hasFeature,
      hasPath,
      getCurrentKeyLimit,
      isLimitReached,
      getPlanName,
    ]
  );

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const ctx = useContext(SubscriptionContext);
  if (!ctx)
    throw new Error("useSubscription must be used within SubscriptionProvider");
  return ctx;
};
